import createClient, { type Client } from 'openapi-fetch';
import type { paths as ApiGatewayPaths } from './types/api-gateway';
import type { paths as AlertPaths } from './types/alert-service';
import type { paths as AnomalyPaths } from './types/anomaly-service';
import type { paths as AssetPaths } from './types/asset-service';
import type { paths as FftPaths } from './types/fft-service';
import type { paths as ReportingPaths } from './types/reporting-service';
import type { paths as SensorGraphPaths } from './types/sensor-graph-service';
import type { paths as SensorIngestionPaths } from './types/sensor-ingestion-service';
import type { paths as TelemetryPaths } from './types/telemetry-query-service';

// Per-screen consumers don't talk to services directly in production —
// the api-gateway is the single front-door. We export per-service typed
// clients anyway so contract tests, Storybook MSW handlers, and Phase 4
// E2E tests can target a service in isolation when it helps.

export type ApiClientConfig = {
  /**
   * Base URL prefix. In dev this typically points at the local Vite
   * proxy ('/api/v1' rewriting to the dev gateway). In prod it's the
   * deployed gateway origin.
   */
  baseUrl: string;
  /** Bearer token supplier. Phase 4 wires this to the auth store. */
  getAuthToken?: () => string | undefined;
};

function makeFetch(config: ApiClientConfig): typeof fetch {
  // Wrap globalThis.fetch to inject the bearer header. openapi-fetch
  // takes a `fetch` override so we can keep the typed client thin.
  return async (input, init) => {
    const headers = new Headers(init?.headers);
    const token = config.getAuthToken?.();
    if (token && !headers.has('Authorization')) {
      headers.set('Authorization', `Bearer ${token}`);
    }
    return globalThis.fetch(input, { ...init, headers });
  };
}

function build<TPaths extends {}>(config: ApiClientConfig): Client<TPaths> {
  return createClient<TPaths>({
    baseUrl: config.baseUrl,
    fetch: makeFetch(config),
  });
}

export type ApiClients = {
  apiGateway: Client<ApiGatewayPaths>;
  alert: Client<AlertPaths>;
  anomaly: Client<AnomalyPaths>;
  asset: Client<AssetPaths>;
  fft: Client<FftPaths>;
  reporting: Client<ReportingPaths>;
  sensorGraph: Client<SensorGraphPaths>;
  sensorIngestion: Client<SensorIngestionPaths>;
  telemetryQuery: Client<TelemetryPaths>;
};

/**
 * Construct the typed clients for every service. The same config is
 * threaded through — Phase 4 will replace `baseUrl` per-service with
 * either the gateway origin or a service-specific base for E2E tests.
 */
export function createApiClients(config: ApiClientConfig): ApiClients {
  return {
    apiGateway: build<ApiGatewayPaths>(config),
    alert: build<AlertPaths>(config),
    anomaly: build<AnomalyPaths>(config),
    asset: build<AssetPaths>(config),
    fft: build<FftPaths>(config),
    reporting: build<ReportingPaths>(config),
    sensorGraph: build<SensorGraphPaths>(config),
    sensorIngestion: build<SensorIngestionPaths>(config),
    telemetryQuery: build<TelemetryPaths>(config),
  };
}
